import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function Module3Page() {
  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--green-dim)] border-b border-[var(--green)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/elevation/workbook/oqf" className="text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)] text-sm mb-4 inline-block">
            ← Back to OQF Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--text-pri)]">
            Module 3: Define the Path of Inquiry
          </h1>
          <p className="text-[var(--text-sec)] mt-2">
            Objective: By the end of this module, you will be able to design a <strong>Smallest Decisive Experiment (SDE)</strong> to quickly test a key assumption.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            💡 Concept: The "Smallest Decisive Experiment" (SDE)
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            Teams often run "experiments" that are too big, too slow, and don't provide a clear answer. An SDE is the antidote.
          </p>
          <p className="text-[var(--text-sec)]">
            An SDE is the <strong>minimum viable learning step</strong> that meaningfully reduces uncertainty. It's the quickest, lowest-cost test that can produce an answer clear enough to inform a real decision.
          </p>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-8">
          <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
            🛠️ Tool: The 3 SDE Criteria
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            A true SDE must meet all three criteria.
          </p>
          <div className="space-y-4">
            <div className="border-l-4 border-[var(--green)] pl-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">1. Small:</h3>
              <ul className="list-disc list-inside text-[var(--text-sec)] space-y-1">
                <li>Can we get a useful signal <em>this week</em> (or even this <em>day</em>)?</li>
                <li>Does it use mockups, conversations, or existing data instead of new code?</li>
              </ul>
            </div>
            <div className="border-l-4 border-[var(--green)] pl-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">2. Decisive:</h3>
              <ul className="list-disc list-inside text-[var(--text-sec)] space-y-1">
                <li>Is there a clear "if-then" rule? (e.g., "If 4/5 customers say X, we will do Y.")</li>
                <li>If the result were the opposite of what we expect, would we <em>actually</em> act differently?</li>
              </ul>
            </div>
            <div className="border-l-4 border-[var(--green)] pl-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">3. Truth-Oriented:</h3>
              <ul className="list-disc list-inside text-[var(--text-sec)] space-y-1">
                <li>Does this test the real unknown (e.g., "willingness to pay")?</li>
                <li>Does it get evidence from a real source of truth (e.g., a customer) instead of a proxy (e.g., our internal opinion)?</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Link to="/elevation/workbook/oqf/module-2" className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]">
            ← Previous Module
          </Link>
          <Link to="/elevation/workbook/oqf/module-4" className="px-4 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)] flex items-center gap-2">
            Next: Module 4
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






